import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Radio } from 'antd'; 
import ListSearchItemsByIds from '../../../updated/components/ListSearchItemsByIds/ListSearchItemsByIds';
import SearchSelector from '../../../updated/components/SearchSelector/SearchSelector';
import { useCreatePrerequisiteMutation, usePatchPrerequisiteMutation, useDeletePrerequisiteMutation } 
    from '../../../gql/hooks/mutations';

const usePrerequisites = ( formController, type ) => { 
    const [ form ] = useState( formController );
    const [ prerequisiteType ] = useState( type );
    const [ createPrerequisite ] = useCreatePrerequisiteMutation();
    const [ patchPrerequisite ] = usePatchPrerequisiteMutation();
    const [ deletePrerequisite ] = useDeletePrerequisiteMutation();
    // const [ prerequisiteOf, setPrerequisiteOf ] = useState( );

    useEffect( () => {
        form.setFields( [
            { name: 'prerequisiteSet', value: false },
            { name: 'prerequisiteIds', value: [] },
            { name: 'prerequisiteAll', value: true },
            { name: 'prerequisiteOf', value: [] }
        ] );
    }, [] );

    const setSelectedPrerequisites = ( data ) => {
        const operator = data?.node?.prerequisiteSet?.operator;
        const prerequisites = data?.node?.prerequisiteSet?.prerequisites || [];

        // We use isPrerequisiteOf to NOT include these in the chooser to prevent circular references
        const isPrerequisiteOf = data?.node?.prerequisiteStatus?.isPrerequisiteOf || [];
        form.setFields( [ { name: 'prerequisiteOf', value: isPrerequisiteOf } ] );

        const sortedPrerequisits = prerequisites.map( i => ( { 
            objectId: i.objectId,
            weight: i.weight
        } ) ).sort( ( a, b ) => a.weight - b.weight );

        if ( prerequisites && operator ) { 
            form.setFields( [
                { name: 'prerequisiteSet', value: {
                    ...data.node.prerequisiteSet,
                    prerequisites: sortedPrerequisits }
                },
                { name: 'prerequisiteAll', value: operator === 'AND' },
                { name: 'prerequisiteIds', value: sortedPrerequisits.map( i => i.objectId ) }
            ] );
        }
    };

    const savePrerequisites = async ( objectId ) => {
        // Input variables for create and patch mutations
        const getMutationInput = () => {
            return {
                objectId, 
                type: prerequisiteType === 'courses' ? 'COURSE' : 'CURRICULUM',
                operator: prerequisiteAll ? 'AND' : 'OR',
                prerequisites: prerequisiteIds.map( ( i, index ) => ( { 
                    objectId: i,
                    weight: index
                } ) )
            };
        };

        const prerequisiteSet = form.getFieldValue( 'prerequisiteSet' );
        const prerequisiteAll = form.getFieldValue( 'prerequisiteAll' );
        const prerequisiteIds = form.getFieldValue( 'prerequisiteIds' );

        /**************************************
         * Create a new prerequisiteSet 
         * if no previous one exists.
         **************************************/

        if ( !prerequisiteSet ) {
            // If there is nothing save return 
            if ( prerequisiteIds.length === 0 ) {
                return true;
            }

            try {
                const response = await createPrerequisite( { variables: { input: getMutationInput() } } );
                const success = !( response?.error || response?.errors && response?.errors?.length > 0 );
            
                if ( success ) {
                    return true;
                } else {
                    console.error( 'Error creating prerequisite!' ); 
                    return false;
                }
            } catch ( e ) {  
                console.error( e );
                return false;
            }
        } 

        /**************************************
         * Delete the prerequisiteSet if a 
         * previous one exists and there are
         * now no prerequisiteIds
         **************************************/

        if ( prerequisiteSet && prerequisiteIds.length === 0 ) {
            try {
                const response = await deletePrerequisite( { variables: { input: { id: prerequisiteSet.id } } } );
                const success = !( response?.error || response?.errors && response?.errors?.length > 0 );
            
                if ( success ) {
                    return true;
                } else {
                    console.error( 'Error deleting prerequisite!' ); 
                    return false;
                }
            } catch ( e ) {  
                console.error( e );
                return false;
            }
        } 
        
        /**************************************
         * Mutate a prerequisiteSet if a
         * previous one exists and there are
         * modificatons
         **************************************/

        if ( prerequisiteSet && prerequisiteIds.length > 0 ) {
            const input = getMutationInput();
        
            try {
                input.id = prerequisiteSet.id;
                
                const response = await patchPrerequisite( { variables: { input } } );
                const success = !( response?.error || response?.errors && response?.errors?.length > 0 );
            
                if ( success ) {
                    return true;
                } else {
                    console.error( 'Error patching prerequisite!' ); 
                    return false;
                }
            } catch ( e ) {  
                console.error( e );
                return false;
            }
        }

        return true;
    };

    const render = ( alwaysDisabledItems ) => (
        <Card 
            className="use-prerequisites"
            title="Prerequisites"
            extra={ 
                <Form.Item 
                    name="prerequisiteAll"
                    style={ { marginBottom: 0 } }
                >
                    <Radio.Group size="small">
                        <Radio.Button value={ true }>All Required</Radio.Button>
                        <Radio.Button value={ false }>One Required</Radio.Button>
                    </Radio.Group>
                </Form.Item>
            }
        >
            <Form.Item 
                name="prerequisiteIds"
                valuePropName="initialItems"
            >
                <SearchSelector 
                    type={ type } 
                    showType={ false } 
                    alwaysDisabledItems={ alwaysDisabledItems }
                />
            </Form.Item>
        
            <Form.Item 
                name="prerequisiteOf"
                valuePropName="ids"
                style={ { marginBottom: 0 } }
            >
                <ListSearchItemsByIds 
                    type={ type } 
                    title="Prerequisite Of"
                />
            </Form.Item>
        </Card>
    );

    return useMemo( () => ( {
        renderPrerequisites: render,
        setSelectedPrerequisites,
        savePrerequisites
    } ), [] );
};

export default usePrerequisites;
